/**
 * Support Admin Dashboard
 * Analytics and management view for Support Admins
 */

import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import {
  getTaskAnalytics,
  getTaskSummaryStats,
  getAgents,
  getUniqueTags,
  getImportBatches,
} from "../../services/supportTasksDb.service";
import type { TaskAnalyticsFilters } from "../../services/supportTasksDb.service";
import type {
  SupportAgent,
  TaskTypeFilterValue,
} from "../../types/supportTasks.types";
import { taskTypeFilterOptions } from "../../types/supportTasks.types";
import { formatDate } from "../../utils/formatters";

interface SupportAdminDashboardProps {
  onOpenImportModal?: () => void;
  onOpenAgentManagement?: () => void;
  onOpenCreateTaskModal?: () => void;
  /** When provided, locks analytics to this agent and hides admin-only features */
  agentId?: string;
  /** Whether this is being shown to an agent (vs admin) */
  isAgentView?: boolean;
  /** When provided, syncs the analytics task type filter with the parent page filter */
  taskType?: TaskTypeFilterValue;
}

const SupportAdminDashboard: React.FC<SupportAdminDashboardProps> = ({
  onOpenAgentManagement,
  onOpenCreateTaskModal,
  agentId,
  isAgentView = false,
  taskType,
}) => {
  const [showImportHistory, setShowImportHistory] = useState(false);
  const [filters, setFilters] = useState<TaskAnalyticsFilters>({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0],
    endDate: new Date().toISOString().split("T")[0],
    taskType: "all",
    // Pre-filter to agent if provided
    agentId: agentId,
  });

  // Keep agentId in sync if prop changes
  React.useEffect(() => {
    if (agentId) {
      setFilters((f) => ({ ...f, agentId }));
    }
  }, [agentId]);

  // Sync task type filter from parent page
  React.useEffect(() => {
    if (taskType !== undefined) {
      setFilters((f) => ({ ...f, taskType }));
    }
  }, [taskType]);

  // Fetch analytics data
  const { data: analyticsData = [], isLoading: analyticsLoading } = useQuery({
    queryKey: ["support-analytics", filters],
    queryFn: () => getTaskAnalytics(filters),
  });

  // Fetch summary stats
  const { data: summaryStats } = useQuery({
    queryKey: ["support-summary", filters],
    queryFn: () => getTaskSummaryStats(filters),
  });

  // Fetch agents for filter dropdown
  const { data: agents = [] } = useQuery({
    queryKey: ["support-agents"],
    queryFn: getAgents,
  });

  // Fetch unique tags for filter
  const { data: availableTags = [] } = useQuery({
    queryKey: ["support-tags"],
    queryFn: getUniqueTags,
  });

  // Fetch import history
  const { data: importBatches = [] } = useQuery({
    queryKey: ["import-batches"],
    queryFn: () => getImportBatches(10),
    enabled: showImportHistory,
  });

  const handleExportData = () => {
    // Export analytics data as CSV
    const headers = ["Date", "New Tasks", "Completed", "Cancelled"];
    const rows = analyticsData.map((d) => [
      d.date,
      d.new_tasks,
      d.completed,
      d.cancelled,
    ]);
    const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `support-tasks-analytics-${filters.startDate}-to-${filters.endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">
          {isAgentView ? "My Task Analytics" : "Task Analytics"}
        </h2>
        <div className="flex items-center gap-3">
          {!isAgentView && (
            <button
              onClick={() => setShowImportHistory(!showImportHistory)}
              className="text-sm text-gray-600 hover:text-gray-900"
            >
              {showImportHistory ? "Hide" : "Show"} Import History
            </button>
          )}
          <button
            onClick={handleExportData}
            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Export CSV
          </button>
          {!isAgentView && onOpenAgentManagement && (
            <button
              onClick={onOpenAgentManagement}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Manage Agents
            </button>
          )}
          {/* Import Tasks button removed — tasks come via API only */}
          {onOpenCreateTaskModal && (
            <button
              onClick={onOpenCreateTaskModal}
              className="px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
            >
              Create Task
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex flex-wrap gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) =>
                setFilters((f) => ({ ...f, startDate: e.target.value }))
              }
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              End Date
            </label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) =>
                setFilters((f) => ({ ...f, endDate: e.target.value }))
              }
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Task Type
            </label>
            <select
              value={filters.taskType || "all"}
              onChange={(e) =>
                setFilters((f) => ({
                  ...f,
                  taskType: e.target.value as TaskTypeFilterValue,
                }))
              }
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg"
            >
              <option value="all">All Types</option>
              {taskTypeFilterOptions().map((group) => (
                <optgroup key={group.label} label={group.label}>
                  {group.options.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
          {/* Agent filter - hidden when in agent view (already filtered) */}
          {!isAgentView && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Agent
              </label>
              <select
                value={filters.agentId || ""}
                onChange={(e) =>
                  setFilters((f) => ({
                    ...f,
                    agentId: e.target.value || undefined,
                  }))
                }
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg"
              >
                <option value="">All Agents</option>
                {agents.map((agent: SupportAgent) => (
                  <option key={agent.id} value={agent.id}>
                    {agent.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Resolution Time
            </label>
            <select
              value={filters.resolutionDays || ""}
              onChange={(e) =>
                setFilters((f) => ({
                  ...f,
                  resolutionDays:
                    (e.target.value as "1" | "2" | "more") || undefined,
                }))
              }
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg"
            >
              <option value="">Any</option>
              <option value="1">Same Day</option>
              <option value="2">2 Days</option>
              <option value="more">More than 2 Days</option>
            </select>
          </div>
          {availableTags.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Resolution Tag
              </label>
              <select
                value={filters.tags?.[0] || ""}
                onChange={(e) =>
                  setFilters((f) => ({
                    ...f,
                    tags: e.target.value ? [e.target.value] : undefined,
                  }))
                }
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg"
              >
                <option value="">All Tags</option>
                {availableTags.map((tag) => (
                  <option key={tag} value={tag}>
                    {tag}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Summary Stats */}
      {summaryStats && (
        <div className="p-4 border-b border-gray-200 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">
              {summaryStats.total_tasks}
            </div>
            <div className="text-xs text-gray-500">Total Tasks</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-amber-600">
              {summaryStats.pending}
            </div>
            <div className="text-xs text-gray-500">Pending</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              {summaryStats.in_progress}
            </div>
            <div className="text-xs text-gray-500">In Progress</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              {summaryStats.completed}
            </div>
            <div className="text-xs text-gray-500">Completed</div>
          </div>
          {/* Cancelled stat removed from overview 2026-05-20. The
              underlying status is still valid on individual tasks; the
              dashboard just doesn't headline it anymore. Cancelled tasks
              can still be reached via the legacy filters on the list. */}
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">
              {summaryStats.overdue_count}
            </div>
            <div className="text-xs text-gray-500">Overdue</div>
          </div>
          <div className="text-center">
            <div
              className={`text-2xl font-bold ${
                summaryStats.unassigned > 0 ? "text-amber-700" : "text-gray-400"
              }`}
            >
              {summaryStats.unassigned}
            </div>
            <div className="text-xs text-gray-500">Unassigned</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">
              {summaryStats.avg_resolution_days.toFixed(1)}
            </div>
            <div className="text-xs text-gray-500">Avg Days to Resolve</div>
          </div>
        </div>
      )}

      {/* Chart */}
      <div className="p-4">
        {analyticsLoading ? (
          <div className="h-64 flex items-center justify-center text-gray-500">
            Loading analytics...
          </div>
        ) : analyticsData.length === 0 ? (
          <div className="h-64 flex items-center justify-center text-gray-500">
            No data available for the selected date range
          </div>
        ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={analyticsData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(d) =>
                    new Date(d).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                    })
                  }
                  fontSize={12}
                />
                <YAxis fontSize={12} />
                <Tooltip
                  labelFormatter={(d) => formatDate(d as string)}
                  contentStyle={{ fontSize: 12 }}
                />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="new_tasks"
                  name="New Tasks"
                  stroke="#6366f1"
                  fill="#c7d2fe"
                  stackId="1"
                />
                <Area
                  type="monotone"
                  dataKey="completed"
                  name="Completed"
                  stroke="#22c55e"
                  fill="#bbf7d0"
                  stackId="2"
                />
                {/* Cancelled chart series removed from overview 2026-05-20
                    along with the matching stat card — Unassigned is the
                    headline metric for leadership triage now. */}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Import History - Admin only */}
      {!isAgentView && showImportHistory && (
        <div className="p-4 border-t border-gray-200">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">
            Recent Imports
          </h3>
          {importBatches.length === 0 ? (
            <p className="text-sm text-gray-500">No import history</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3 font-medium text-gray-600">
                      File
                    </th>
                    <th className="text-left py-2 px-3 font-medium text-gray-600">
                      Date
                    </th>
                    <th className="text-right py-2 px-3 font-medium text-gray-600">
                      Rows
                    </th>
                    <th className="text-right py-2 px-3 font-medium text-gray-600">
                      Created
                    </th>
                    <th className="text-right py-2 px-3 font-medium text-gray-600">
                      Errors
                    </th>
                    <th className="text-left py-2 px-3 font-medium text-gray-600">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {importBatches.map((batch) => (
                    <tr key={batch.id} className="border-b">
                      <td className="py-2 px-3">{batch.file_name}</td>
                      <td className="py-2 px-3">
                        {formatDate(batch.created_at)}
                      </td>
                      <td className="py-2 px-3 text-right">
                        {batch.total_rows}
                      </td>
                      <td className="py-2 px-3 text-right text-green-600">
                        {batch.tasks_created}
                      </td>
                      <td className="py-2 px-3 text-right text-red-600">
                        {batch.errors}
                      </td>
                      <td className="py-2 px-3">
                        <span
                          className={`px-2 py-0.5 text-xs rounded-full ${
                            batch.status === "completed"
                              ? "bg-green-100 text-green-700"
                              : batch.status === "failed"
                                ? "bg-red-100 text-red-700"
                                : "bg-yellow-100 text-yellow-700"
                          }`}
                        >
                          {batch.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SupportAdminDashboard;
